import { useMemo } from 'react';

import Calendar from '@publicImages/svg/calendar.svg?icon';

import AlmostSoldOut from '@/components/AlmostSoldOut';
import ContextMenu from '@/components/elements/ContextMenu';
import PromoBannerPlace from '@/components/PromoBanner/Place';
import UIResource from '@/components/UIResource';
import WithFrenchContent from '@/components/WithFrenchContent';
import {
  findSwappedPort,
  getPortsFromDefaultPackOfGrouped,
} from '@/ducks/pages/chooseVoyage/groupPackagesWithSameNames';
import { selectIsSailingDataLoaded, selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { PromoBannerPlaceId } from '@/features/promoBanners/types';
import { formatDate, parse } from '@/helpers/util/dateUtil';
import withSkeleton from '@/hocs/withSkeleton';
import { useAppSelector } from '@/store';

import FullCruisePortsOfCall from '../FullCruisePortsOfCall';
import PortMessage, { PORT_MESSAGE_FORMATS } from '../PortMessage';
import PreCheckOutSummaryModuleSkeleton from './Skeleton';

import './PreCheckOutSummaryModule.scss';

const getFormattedDates = (startDate, endDate) => {
  let formattedStartDate = formatDate(startDate, 'MMM dd');
  let formattedEndDate = formatDate(endDate, '-dd, yyyy');
  if (parse(startDate).getFullYear() !== parse(endDate).getFullYear()) {
    formattedStartDate = formatDate(startDate, 'MMM dd, yyyy');
    formattedEndDate = formatDate(endDate, ' - MMM dd, yyyy');
  } else if (parse(startDate).getMonth() !== parse(endDate).getMonth()) {
    formattedEndDate = formatDate(endDate, ' - MMM dd, yyyy');
  }
  return { formattedEndDate, formattedStartDate };
};

const PreCheckOutSummaryModule = ({ isCelebrations, isCharter, onChangeDates, packageData }) => {
  const sailingData = useAppSelector(selectSailingData);
  const { duration, endDate, id, name, ship, startDate } = sailingData;

  const { formattedEndDate, formattedStartDate } = useMemo(
    () => getFormattedDates(startDate, endDate),
    [startDate, endDate],
  );

  // Note: Cannot take ports of current sailing object as it will have ports from /sailings api which has detailed ports than the one available in /voyages api
  const portsFromvoyagesOfSailing = packageData?.sailingList?.find((sailing) => sailing.id === id)?.ports || [];
  const swappedPortData = findSwappedPort(
    getPortsFromDefaultPackOfGrouped(packageData),
    portsFromvoyagesOfSailing,
    true,
  );

  return (
    <>
      <div className="PreCheckOutSummaryModule">
        <div className="PreCheckOutSummaryModuleMain">
          <div className="itineraryDetails">
            <div className="itineraryHeading">
              <div className="itineraryHeading__sailing">
                <span className="itineraryHeading__title">
                  <UIResource id="AdvancedRefinement.label" values={{ durations: duration }} />
                  <span className="shipName">{ship?.name}</span>
                </span>

                <AlmostSoldOut
                  classificationCodes={sailingData.classificationCodes}
                  className="itineraryHeading__almostSoldOut"
                  messageId="AlmostSoldOut.SailingLevel.Message"
                />
              </div>

              {!isCelebrations && !isCharter && (
                <ContextMenu
                  className="contextMenu"
                  onSelect={(e) => {
                    e.preventDefault();
                    onChangeDates();
                  }}
                >
                  <Calendar aria-hidden />
                  <UIResource id="Summary.Context.datelabel" />
                </ContextMenu>
              )}
            </div>
            <WithFrenchContent className="itineraryName" node="h2" text={name} />
            <div className="itineraryDate">
              <span>{formattedStartDate}</span>
              <span>{formattedEndDate}</span>
            </div>
          </div>
        </div>
      </div>
      <PromoBannerPlace placeId={PromoBannerPlaceId.preCheckoutB} />
      <div className="PreCheckOutSummaryModule__PortsOfCall">
        <FullCruisePortsOfCall isHeadingShown={false} showItineraryToggle>
          {swappedPortData !== null && packageData?.isPortChangeAvailable && (
            <PortMessage format={PORT_MESSAGE_FORMATS.DETAILS_MODULE} swappedPort={swappedPortData} />
          )}
        </FullCruisePortsOfCall>
      </div>
    </>
  );
};

export default withSkeleton(PreCheckOutSummaryModule, {
  selector: (state, { searchParams }) => selectIsSailingDataLoaded(state, searchParams),
  skeleton: PreCheckOutSummaryModuleSkeleton,
});
